import React from 'react';
import moment from 'moment';
import { Tooltip, Button, FormLabel, FormGroup, Select, MenuItem, Box } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Info as InfoIcon } from '@mui/icons-material';
import BaseMUIDataTable from '../../../../../components/BaseMUIDataTable/BaseMUIDataTable';
import { Colors } from '../../../../../assets/theme/Colors';
import { formatDate, formatTime } from '../../../../../helpers/formatters';
import {
  getRescueClaimer,
  getRescuePickupLocationFullName,
  getRescuePosition, isRescueClaimed,
  showAdoptRescueButton,
  showClaimRescueButton,
} from '../../../../../helpers/RescuesHelper';
import { getMuiTableDataIndex } from '../../../../../helpers/getters';
import RescuesTableStatusBox from '../../../../../components/Common/RescuesTableStatusBox';
import useHasActiveFeatureFlag from '../../../../../hooks/useFeatureFlags';
import { FF_DONOR_LINK } from '../../../../../components/FeatureFlagWrapper';
import DonorLinkPickupIndicator from '../../../../../components/DonorLinkPickupIndicator';
import useActiveUser from '../../../../../hooks/useActiveUser';
import { sortByPickupReady } from '../../../../../helpers/sorters';

const customActionsButtonsSx = {
  marginRight: '6px',
  fontSize: '11px',
  padding: '6px',
};

const RescuerDashboardScheduleRescuesTable = ({
  tableId,
  rescues,
  isRescuingPaused,
  onClaimRescueButtonClick,
  onAdoptRescueButtonClick,
  onCellClick,
}) => {
  const activeUser = useActiveUser();
  const hasActiveDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);
  const pinnedRowIds = rescues.filter((rescue) => (rescue?.donation_groups || []).length > 0).map((r) => r.id);
  const dateFormat = 'MM/DD/YYYY';

  const sortPinnedFirst = (a, b, colIndex, order) => {
    let valA = a.data[colIndex];
    let valB = b.data[colIndex];

    if (valA && moment(valA, dateFormat, true).isValid()) {
      valA = moment(valA, dateFormat);
      valB = moment(valB, dateFormat);
      return order === 'asc' ? valA - valB : valB - valA;
    }

    if (valA < valB) {
      return order === 'asc' ? -1 : 1;
    }

    if (valA > valB) {
      return order === 'asc' ? 1 : -1;
    }

    return 0;
  };

  const pickupReadyCustomBodyRender = (value, tableMeta) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return null;
    }

    if (!rescue?.location_donor_link_active) {
      return '-';
    }

    return <DonorLinkPickupIndicator pickupReady={!!value} />;
  };

  return (
    <ThemeProvider
      theme={(theme) =>
        createTheme({
          ...theme,
          components: {
            ...theme.components,
            MUIDataTableHeadCell: {
              styleOverrides: {
                root: {
                  ...theme.components.MUIDataTableHeadCell.styleOverrides.root,
                  '&:last-child': {
                    width: 175,
                  },
                },
              }
            },
          },
      })}
    >
      <BaseMUIDataTable
        tableId={tableId}
        data={rescues}
        columns={[
          {
            name: 'id',
            label: 'ID',
            options: {
              filter: false,
              sort: false,
              display: false,
              viewColumns: false,
            },
          },
          {
            name: 'pinned',
            label: ' ',
            options: {
              display: true,
              viewColumns: false,
              filter: false,
              sort: false,
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];
                const rescueHasDonationGroups = (rescue?.donation_groups || []).length > 0;

                return (
                  <>
                    {rescueHasDonationGroups && (
                      <Box display="flex" alignItems="center">
                        <Tooltip arrow placement="top" title="This rescue is reserved for a small group of volunteers in your site, and you’re one of them! Please claim if you can.">
                          <InfoIcon htmlColor={Colors.blue.main} />
                        </Tooltip>
                      </Box>
                    )}
                  </>
                )
              }
            },
          },
          {
            name: 'date',
            label: 'Date',
            options: {
              forceDisplay: true,
              filterOptions: {
                renderValue: formatDate,
              },
              customFilterListOptions: {
                render: value => `Date: ${formatDate(value)}`,
              },
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                return (
                  <>
                    <span>
                      {moment(value, 'YYYYMMDD').format(dateFormat)}
                    </span>

                    {rescue && rescue.cancelled_by_id && (
                      <>
                        <br />

                        <span style={{ color: Colors.errorText, fontWeight: 'bold' }}>
                          Cancelled
                        </span>
                      </>
                    )}
                  </>
                );
              },
            },
          },
          {
            name: 'day',
            label: 'Day',
            options: {
              filterType: 'custom',
              filterOptions: {
                logic: (dayOfWeek, filters) => {
                  if (!filters || filters.length === 0) {
                    return false;
                  }

                  return !filters.includes(dayOfWeek);
                },
                display: (filterList, onChange, index, column) => (
                  <>
                    <FormLabel>Day</FormLabel>
                    <FormGroup>
                      <Select
                        variant="standard"
                        fullWidth
                        value={filterList[index][0] || 'All'}
                        onChange={event => {
                          filterList[index][0] = event.target.value;
                          onChange(filterList[index][0] !== 'All' ? filterList[index] : [], index, column);
                        }}
                      >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="Sunday">Sunday</MenuItem>
                        <MenuItem value="Monday">Monday</MenuItem>
                        <MenuItem value="Tuesday">Tuesday</MenuItem>
                        <MenuItem value="Wednesday">Wednesday</MenuItem>
                        <MenuItem value="Thursday">Thursday</MenuItem>
                        <MenuItem value="Friday">Friday</MenuItem>
                        <MenuItem value="Saturday">Saturday</MenuItem>
                      </Select>
                    </FormGroup>
                  </>
                ),
              },
              customFilterListOptions: {
                render: value => `Day: ${value}`,
              },
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRender: (value, tableMeta) =>
                moment(rescues[getMuiTableDataIndex(tableMeta)].date, 'YYYYMMDD').format('dddd'),
            },
          },
          {
            name: 'pickup_begin',
            label: 'Pickup Begin',
            options: {
              filter: false,
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRender: value => formatTime(value),
            },
          },
          {
            name: 'pickup_end',
            label: 'Pickup End',
            options: {
              filter: false,
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRender: value => formatTime(value),
            },
          },
          {
            name: 'location',
            label: 'Food Donor',
            options: {
              customFilterListOptions: {
                render: value => `Food Donor: ${value}`,
              },
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue) {
                  return null;
                }

                const claimer = getRescueClaimer(rescue);
                const rescuerIsRescueClaimer = activeUser.id === claimer?.id;

                if (isRescueClaimed(rescue) && !rescuerIsRescueClaimer) {
                  return null;
                }

                return getRescuePickupLocationFullName(rescue);
              },
            },
          },
          {
            name: 'receiver',
            label: 'Receiver',
            options: {
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue) {
                  return null;
                }

                const claimer = getRescueClaimer(rescue);
                const rescuerIsRescueClaimer = activeUser.id === claimer?.id;

                if (isRescueClaimed(rescue) && !rescuerIsRescueClaimer) {
                  return null;
                }

                return value || '';
              },
            },
          },
          {
            name: 'pickup_city',
            label: 'Pickup City',
            options: {
              display: false,
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue) {
                  return null;
                }

                return `${rescue.pickup_city}, ${rescue.pickup_st}`;
              },
            },
          },
          {
            name: 'dropoff_city',
            label: 'Dropoff City',
            options: {
              display: false,
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue) {
                  return null;
                }

                return `${rescue.dropoff_city}, ${rescue.dropoff_st}`;
              },
            },
          },
          ...(hasActiveDonorLinkFF
            ? [
                {
                  name: 'rescue_confirmed',
                  label: 'Donor Link',
                  options: {
                    display: true,
                    filter: true,
                    sort: true,
                    sortCompare: (order) => (row1, row2) => {
                      const rescue1 = rescues.find((fd) => fd.id === row1.rowData[0]);
                      const rescue2 = rescues.find((fd) => fd.id === row2.rowData[0]);

                      return sortByPickupReady(order, rescue1, rescue2);
                    },
                    customBodyRender: (value, tableMeta) => pickupReadyCustomBodyRender(value, tableMeta),
                  },
                },
              ]
            : []),
          {
            name: 'slug',
            label: 'Description',
            options: {
              filter: false,
            },
          },
          {
            name: 'rescue_size',
            label: 'Car Size',
            options: {
              customBodyRender: value => value || '',
            },
          },
          {
            name: 'position',
            label: 'Position',
            options: {
              filter: false,
              display: false,
              customBodyRenderLite: dataIndex => getRescuePosition(rescues[dataIndex]),
            },
          },
          {
            name: 'actions',
            label: 'Actions',
            options: {
              download: false,
              Empty: true,
              filter: false,
              searchable: false,
              viewColumns: false,
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue) {
                  return false;
                }

                if (isRescueClaimed(rescue)) {
                  return <RescuesTableStatusBox rescue={rescue} />
                }

                // the rescuer can be paused at the rescue's site only
                const rescuingPaused = isRescuingPaused(rescue);

                return (
                  <>
                    {showClaimRescueButton(rescue) && (
                      <Tooltip
                        placement="top"
                        key="I'll do it"
                        title={rescuingPaused ? "Resume rescuing to claim rescue" : "Claim rescue"}
                        sx={customActionsButtonsSx}
                      >
                        <Button
                          // Set pointer event for when the butons are disabled
                          style={{pointerEvents: "auto"}}
                          component={rescuingPaused ? "div" : "button"}
                          data-testid="claim-rescue"
                          disabled={rescuingPaused}
                          type="button"
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={rescuingPaused ? null : () => onClaimRescueButtonClick(rescue)}
                        >
                          I'll do it
                        </Button>
                      </Tooltip>
                    )}

                    {showAdoptRescueButton(rescue) && (
                      <Tooltip
                        placement="top"
                        key="Adopt"
                        title={rescuingPaused ? "Resume rescuing to adpot rescue" : "Adopt rescue"}
                        sx={customActionsButtonsSx}
                      >
                        <Button
                          // Set pointer event for when the butons are disabled
                          style={{pointerEvents: "auto"}}
                          component={rescuingPaused ? "div" : "button"}
                          disabled={rescuingPaused}
                          type="button"
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={rescuingPaused ? null : () => onAdoptRescueButtonClick(rescue)}
                          >
                          Adopt
                        </Button>
                      </Tooltip>
                    )}
                  </>
                );
              },
            },
          },
        ]}
        options={{
          sortOrder: {
            name: 'date',
            direction: 'asc',
          },
          filter: false,
          download: false,
          elevation: 0,
          rowsPerPage: 15,
          selectableRows: 'none',
          responsive: 'simple',
          customSort: (data, colIndex, order) => {
            const pinnedData = data.filter((row) => pinnedRowIds.includes(row.data[0]));
            const unpinnedData = data.filter((row) => !pinnedRowIds.includes(row.data[0]));

            return [
              // always display sorted pinned rows first
              ...pinnedData.sort((a, b) => sortPinnedFirst(a, b, colIndex, order)),
              ...unpinnedData.sort((a, b) => sortPinnedFirst(a, b, colIndex, order)),
            ];
          },
          onCellClick: (colData, cellMeta) => {
            // ignore click on "actions" column cell
            if (cellMeta.colIndex >= 9) {
              return false;
            }

            const rescue = rescues[cellMeta.dataIndex];

            if (!rescue || isRescueClaimed(rescue)) {
              return false;
            }

            return onCellClick(rescue);
          },
          setRowProps: (row, dataIndex) => {
            const rescue = rescues[dataIndex];

            if (rescue && isRescueClaimed(rescue)) {
              return {
                sx: {
                  '& > td:not(:last-child)': {
                    opacity: 0.3,
                    WebkitUserSelect: 'none',
                    msUserSelect: 'none',
                    userSelect: 'none',
                  },
                },
              };
            }

            if (rescue && (rescue?.donation_groups || []).length > 0) {
              return {
                sx: {
                  '& > td': {
                    backgroundColor: Colors.donationGroups.light,
                    borderColor: Colors.donationGroups.main,
                  },
                  '&:hover > td': {
                    backgroundColor: Colors.donationGroups.main,
                  },
                },
              };
            }

            if (!rescue || !rescue.cancelled_by_id) {
              return false;
            }

            return {
              sx: {
                '& > td': {
                  opacity: 0.25,
                },
              },
            };
          },
        }}
      />
    </ThemeProvider>
  );
};

export default RescuerDashboardScheduleRescuesTable;
